import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'super_admin' | 'owner' | 'admin' | 'user'
}

interface Company {
  id: string
  companyId?: string
  name: string
}

const API_BASE_URL = 'https://universal-ai-agent-backend.diypa571.workers.dev'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const company = ref<Company | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => {
    return !!token.value && !!user.value
  })

  const login = async (email: string, password: string) => {
    loading.value = true
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Login failed')
      }

      const data = await response.json()
      
      if (!data.success || !data.data) {
        throw new Error('Invalid response format')
      }

      token.value = data.data.token
      user.value = data.data.user
      company.value = data.data.company

      // Store in localStorage
      localStorage.setItem('token', data.data.token)
      localStorage.setItem('user', JSON.stringify(data.data.user))
      if (data.data.company) {
        localStorage.setItem('company', JSON.stringify(data.data.company))
      }

      return data.data
    } catch (error) {
      console.error('Login error:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const register = async (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    companyName: string
    industry: string
  }) => {
    loading.value = true
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
          companyName: data.companyName,
          industry: data.industry,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Registration failed')
      }

      const responseData = await response.json()
      
      if (!responseData.success || !responseData.data) {
        throw new Error('Invalid response format')
      }

      // After registration, user is automatically logged in
      token.value = responseData.data.token
      user.value = responseData.data.user
      company.value = responseData.data.company

      // Store in localStorage
      localStorage.setItem('token', responseData.data.token)
      localStorage.setItem('user', JSON.stringify(responseData.data.user))
      if (responseData.data.company) {
        localStorage.setItem('company', JSON.stringify(responseData.data.company))
      }

      return responseData.data
    } catch (error) {
      console.error('Registration error:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      if (token.value) {
        // TODO: Call logout API when implemented
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear local state
      user.value = null
      company.value = null
      token.value = null

      // Clear localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('company')
    }
  }

  const checkAuth = async (): Promise<boolean> => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    if (!storedToken || !storedUser) {
      // No token or user in storage, definitely not authenticated
      token.value = null
      user.value = null
      company.value = null
      initialized.value = true
      return false
    }

    try {
      // Validate token with backend
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        // Token is invalid, clear storage
        await logout()
        initialized.value = true
        return false
      }

      const data = await response.json()
      
      if (data.success && data.data) {
        token.value = storedToken
        user.value = data.data.user
        company.value = data.data.company || null
        
        // Update localStorage with fresh data
        localStorage.setItem('token', storedToken)
        localStorage.setItem('user', JSON.stringify(data.data.user))
        if (data.data.company) {
          localStorage.setItem('company', JSON.stringify(data.data.company))
        }
        
        initialized.value = true
        return true
      }

      await logout()
      initialized.value = true
      return false
    } catch (error) {
      console.error('Auth check error:', error)
      // On error, clear storage to be safe
      await logout()
      initialized.value = true
      return false
    }
  }

  const initialize = async (): Promise<void> => {
    if (initialized.value) return
    
    loading.value = true
    try {
      await checkAuth()
    } finally {
      loading.value = false
    }
  }

  const setAuthData = (newToken: string, newUser: User, newCompany: Company | null) => {
    token.value = newToken
    user.value = newUser
    company.value = newCompany
    
    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
    if (newCompany) {
      localStorage.setItem('company', JSON.stringify(newCompany))
    }
  }

  return {
    user,
    company,
    token,
    loading,
    initialized,
    isAuthenticated,
    login,
    register,
    logout,
    checkAuth,
    initialize,
    setAuthData,
  }
})